import React from "react";
import { Link, NavLink } from "react-router-dom";
import { MapPin, ArrowRight, MoveRight } from "lucide-react";
import { projects } from "../constants/projects";
import AnimatedSection from "./common/AnimatedSection";

function ProjectCard({ project, featured = false }) {
  return (
    <Link
      to={`/projects/${project.slug}`}
      className="group relative overflow-hidden rounded-2xl block h-full"
      style={{ minHeight: featured ? '480px' : '260px' }}
    >
      {/* Image */}
      <img
        src={project.coverImage}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        loading="lazy"
        decoding="async"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10 group-hover:from-black/90 transition-all duration-300" />

      {/* Category badge */}
      <span className="absolute top-4 left-4 bg-white/15 backdrop-blur-md border border-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
        {project.category}
      </span>

      {/* Status dot */}
      <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/30 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
        {project.status}
      </span>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className={`font-bold text-white leading-tight mb-1.5 ${featured ? 'text-2xl' : 'text-lg'}`}>
          {project.title}
        </h3>
        <div className="flex items-center gap-1.5 text-gray-300 text-xs">
          <MapPin size={11} className="shrink-0" />
          <span>{project.location}</span>
        </div>

        {/* View project — slides up on hover */}
        <div className="mt-4 flex items-center gap-2 text-teal-400 text-sm font-medium translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          View Project <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-200" />
        </div>
      </div>
    </Link>
  );
}

function Projects({ isPage = false }) {
  return (
    <AnimatedSection id="projects" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-teal-500 shadow-[0_0_8px_3px_rgba(20,184,166,0.7)]" />
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_6px_2px_rgba(20,184,166,0.5)]" />
                <span className="w-1 h-1 rounded-full bg-teal-300 shadow-[0_0_4px_1px_rgba(20,184,166,0.3)]" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600">
                Our Portfolio
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              {isPage ? 'All ' : 'Featured '}<span className="text-teal-600">Projects</span>
            </h2>
            <p className="mt-3 text-gray-500 text-sm md:text-base max-w-lg">
              Completed and ongoing developments that blend design, innovation, and lasting impact.
            </p>
          </div>

          {!isPage && (
            <NavLink
              to="/projects"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-200 hover:border-teal-500 text-gray-600 hover:text-teal-600 text-sm font-medium rounded-lg transition-all duration-200 self-start md:self-auto whitespace-nowrap hover:-translate-y-0.5"
            >
              All Projects <MoveRight size={15} />
            </NavLink>
          )}
        </div>

        {isPage ? (
          /* Projects page — uniform grid of all projects */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div key={project.slug} style={{ minHeight: '300px' }}>
                <ProjectCard project={project} />
              </div>
            ))}
          </div>
        ) : (
          /* Homepage — featured (left, full height) + 4 cards in 2×2 grid (right) */
          <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-[1fr_1fr] gap-4" style={{ minHeight: '560px' }}>

            {projects[0] && (
              <div className="md:col-start-1 md:row-span-2 h-full min-h-[480px] md:min-h-0">
                <ProjectCard project={projects[0]} featured />
              </div>
            )}

            {projects[1] && (
              <div className="md:col-start-2 md:row-start-1">
                <ProjectCard project={projects[1]} />
              </div>
            )}
            {projects[2] && (
              <div className="md:col-start-3 md:row-start-1">
                <ProjectCard project={projects[2]} />
              </div>
            )}

            {projects[3] && (
              <div className="md:col-start-2 md:row-start-2">
                <ProjectCard project={projects[3]} />
              </div>
            )}
            {projects[4] && (
              <div className="md:col-start-3 md:row-start-2">
                <ProjectCard project={projects[4]} />
              </div>
            )}
          </div>
        )}

        {/* Bottom CTA — homepage only */}
        {!isPage && (
          <div className="mt-10 text-center">
            <NavLink
              to="/projects"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm hover:-translate-y-0.5"
            >
              Explore All Projects <ArrowRight size={15} />
            </NavLink>
          </div>
        )}

      </div>
    </AnimatedSection>
  );
}

export default Projects;
